import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Check, Zap, MessageCircle, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const PaywallCtx = createContext(null);

const REASON_META = {
    swipe_limit: {
        icon: Zap,
        title: "You're swiping hot 🔥",
        subtitle: "Free swipes reset tomorrow — or unlock unlimited now.",
    },
    chat_limit: {
        icon: MessageCircle,
        title: "One's a taste, unlimited's the vibe",
        subtitle: "Message all your matches — not just the first one.",
    },
    listing_limit: {
        icon: Home,
        title: "Listing more? Go Premium",
        subtitle: "Free members can post one listing. Premium posts unlimited.",
    },
    generic: {
        icon: Sparkles,
        title: "Unlock everything",
        subtitle: "One tap, all the good stuff.",
    },
};

export function PaywallProvider({ children }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("generic");
    const [customMessage, setCustomMessage] = useState(null);
    const [status, setStatus] = useState(null);

    const openPaywall = useCallback(({ reason, message } = {}) => {
        setReason(reason || "generic");
        setCustomMessage(message || null);
        setOpen(true);
    }, []);

    const closePaywall = useCallback(() => {
        setOpen(false);
        setCustomMessage(null);
    }, []);

    // Load subscription status when user is available (also refetches on user change)
    useEffect(() => {
        if (!user) {
            setStatus(null);
            return;
        }
        api.get("/subscription/status")
            .then(({ data }) => setStatus(data))
            .catch(() => {});
    }, [user]);

    const refreshStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/subscription/status");
            setStatus(data);
            return data;
        } catch (e) {
            return null;
        }
    }, []);

    return (
        <PaywallCtx.Provider value={{ openPaywall, closePaywall, status, refreshStatus }}>
            {children}
            <UpgradeModal
                open={open}
                reason={reason}
                customMessage={customMessage}
                status={status}
                onClose={closePaywall}
            />
        </PaywallCtx.Provider>
    );
}

export function usePaywall() {
    return useContext(PaywallCtx);
}

/**
 * handleApiError — call this in a try/catch after any API call that might return 402.
 * Returns true if a paywall was triggered (and shown), false otherwise.
 */
export function useApiErrorHandler() {
    const { openPaywall } = usePaywall();
    return useCallback(
        (err) => {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 402 && d && typeof d === "object" && d.code === "paywall") {
                openPaywall({ reason: d.reason, message: d.message });
                return true;
            }
            return false;
        },
        [openPaywall],
    );
}

function UpgradeModal({ open, reason, customMessage, status, onClose }) {
    const meta = REASON_META[reason] || REASON_META.generic;
    const Icon = meta.icon;
    const [loading, setLoading] = useState(false);
    const price = status?.price_inr ?? 99;

    const startCheckout = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Couldn't start checkout");
            }
        } catch (e) {
            if (e?.response?.status === 409) {
                toast.success(e.response.data?.detail || "Everything's free during launch week 🎉");
                onClose?.();
            } else {
                toast.error("Couldn't start checkout");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[80] grid place-items-center bg-background/85 backdrop-blur-xl p-5"
                    data-testid="upgrade-modal"
                >
                    <div className="blob bg-primary" style={{ width: 280, height: 280, top: 40, right: -60 }} />
                    <motion.div
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        transition={{ type: "spring", stiffness: 240, damping: 22 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative z-10 w-full max-w-md rounded-4xl border border-border bg-card p-7"
                    >
                        <button
                            onClick={onClose}
                            data-testid="upgrade-modal-close"
                            className="absolute top-4 right-4 h-9 w-9 rounded-full hover:bg-accent grid place-items-center"
                        >
                            <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>

                        <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
                            <Icon className="h-7 w-7" strokeWidth={2.5} />
                        </div>

                        <h1 className="mt-4 font-display text-3xl font-black tracking-tighter" data-testid="upgrade-title">
                            {meta.title}
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground" data-testid="upgrade-subtitle">
                            {customMessage || meta.subtitle}
                        </p>

                        <div className="mt-6 rounded-3xl border border-border bg-accent/50 p-5">
                            <div className="text-xs font-bold uppercase tracking-widest text-accent-foreground">
                                Flatch Premium
                            </div>
                            <div className="mt-1 flex items-baseline gap-1">
                                <span className="font-display text-5xl font-black tracking-tighter text-gradient-purple">₹{price}</span>
                                <span className="text-sm text-muted-foreground font-semibold">/month</span>
                            </div>
                            <ul className="mt-4 space-y-2 text-sm">
                                {[
                                    "Unlimited daily swipes",
                                    "Message every match, not just the first",
                                    "Post unlimited flat listings",
                                    "Priority in the roommate feed",
                                    "Cancel anytime",
                                ].map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <div className="mt-0.5 h-5 w-5 rounded-full bg-primary text-primary-foreground grid place-items-center flex-shrink-0">
                                            <Check className="h-3 w-3" strokeWidth={3} />
                                        </div>
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <Button
                            onClick={startCheckout}
                            disabled={loading}
                            data-testid="upgrade-checkout-btn"
                            className="mt-6 h-14 w-full rounded-2xl font-semibold text-base lift"
                        >
                            {loading ? "Opening checkout…" : (<><Sparkles className="mr-2 h-5 w-5" /> Go Premium — ₹{price}/mo</>)}
                        </Button>
                        <button
                            onClick={onClose}
                            data-testid="upgrade-maybe-later-btn"
                            className="mt-3 text-sm text-muted-foreground w-full text-center font-semibold hover:text-foreground"
                        >
                            Maybe later
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
